import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Mail,
  MessageSquare,
  Settings,
  Send,
  Eye,
  Trash2,
  BarChart3,
  Clock,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Edit,
  Plus,
} from 'lucide-react';
import {
  notificationService,
  NotificationTemplate,
  Notification,
} from '../services/notificationService';
type ConsoleTab = 'templates' | 'notifications' | 'attendance' | 'statistics'

interface NotificationManagementProps {
  activeTab?: ConsoleTab
  canLead: boolean
}

const CONSOLE_TABS: { id: ConsoleTab; name: string; icon: typeof Bell; leadersOnly: boolean }[] = [
  { id: 'templates', name: 'Templates', icon: Settings, leadersOnly: true },
  { id: 'notifications', name: 'Notifications', icon: Bell, leadersOnly: false },
  { id: 'attendance', name: 'Attendance', icon: Calendar, leadersOnly: true },
  { id: 'statistics', name: 'Statistics', icon: BarChart3, leadersOnly: false },
]

// The kinds of notice the school sends and the ways it sends them, as the server defines them.
const TEMPLATE_TYPES: [string, string][] = [
  ['fee_reminder', 'Fee reminder'], ['payment_success', 'Payment received'], ['payment_failed', 'Payment failed'],
  ['prn_generated', 'PRN generated'], ['prn_expiring', 'PRN expiring'], ['payment_overdue', 'Payment overdue'],
  ['attendance_absent', 'Learner absent'], ['attendance_late', 'Learner late'], ['attendance_poor', 'Poor attendance'],
  ['attendance_improved', 'Attendance improved'], ['attendance_summary', 'Weekly attendance summary'], ['general', 'General'],
]
type Channel = NotificationTemplate['channel']
const CHANNELS: [Channel, string][] = [['sms', 'SMS'], ['email', 'Email'], ['both', 'Both']]

// Templates and attendance runs are refused to anyone outside school leadership, so only leaders are offered them.
export function notificationConsoleTabs(canLead: boolean) {
  return CONSOLE_TABS.filter(tab => canLead || !tab.leadersOnly)
}

export default function NotificationManagement({ activeTab: initialTab, canLead }: NotificationManagementProps) {
  const [activeTab, setActiveTab] = useState<ConsoleTab>(initialTab ?? (canLead ? 'templates' : 'notifications'))
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null)
  const [templateFormOpen, setTemplateFormOpen] = useState(false)
  const [templateError, setTemplateError] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterChannel, setFilterChannel] = useState<string>('all')

  const queryClient = useQueryClient()

  // Queries
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => notificationService.getTemplates(),
    enabled: canLead,
    retry: 3,
    staleTime: 5 * 60 * 1000,
  })

  const { data: notifications, isLoading: notificationsLoading } = useQuery({
    queryKey: ['notifications', { status: filterStatus, channel: filterChannel }],
    queryFn: () => notificationService.getNotifications({
      status: filterStatus === 'all' ? undefined : filterStatus,
      channel: filterChannel === 'all' ? undefined : filterChannel,
    }),
    retry: 3,
    staleTime: 2 * 60 * 1000,
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['notification-stats'],
    queryFn: () => notificationService.getNotificationStats(30),
    retry: 3,
    staleTime: 5 * 60 * 1000,
  })

  const { data: attendanceStats, isLoading: attendanceStatsLoading } = useQuery({
    queryKey: ['attendance-notification-stats'],
    queryFn: () => notificationService.getAttendanceNotificationStats(30),
    enabled: canLead,
    retry: 3,
    staleTime: 5 * 60 * 1000,
  })

  // Mutations
  const processAttendanceNotificationsMutation = useMutation({
    mutationFn: notificationService.processAttendanceNotifications,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['attendance-notification-stats'] })
    },
  })

  const sendWeeklySummariesMutation = useMutation({
    mutationFn: notificationService.sendWeeklyAttendanceSummaries,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['attendance-notification-stats'] })
    },
  })

  const createTemplatesMutation = useMutation({
    mutationFn: notificationService.createAttendanceTemplates,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
    },
  })

  // Writing a template was only possible in the Django admin until now.
  const saveTemplate = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Partial<NotificationTemplate> }) => (
      id ? notificationService.updateTemplate(id, data) : notificationService.createTemplate(data)
    ),
    onSuccess: () => {
      setTemplateFormOpen(false)
      setEditingTemplate(null)
      setTemplateError('')
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
    },
    onError: () => setTemplateError('The template could not be saved. Check the fields and try again.'),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: notificationService.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
    },
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'text-green-600 bg-green-100'
      case 'delivered': return 'text-blue-600 bg-blue-100'
      case 'failed': return 'text-red-600 bg-red-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      case 'read': return 'text-purple-600 bg-purple-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'sms': return <MessageSquare className="w-4 h-4" />
      case 'email': return <Mail className="w-4 h-4" />
      default: return <Bell className="w-4 h-4" />
    }
  }


  const tabs = notificationConsoleTabs(canLead)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">What the school sends</h2>
          <p className="mt-1 text-sm text-gray-500">
            {canLead ? 'Manage notification templates, send alerts, and track delivery' : 'Track delivery of the notifications the school sends'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {activeTab === 'templates' && canLead && (
            <button
              onClick={() => { setEditingTemplate(null); setTemplateError(''); setTemplateFormOpen(true) }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              New template
            </button>
          )}
          {activeTab === 'attendance' && (
            <button
              onClick={() => createTemplatesMutation.mutate()}
              disabled={createTemplatesMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Create Templates
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {templatesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading templates...</p>
            </div>
          ) : templates && templates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template: NotificationTemplate) => (
                <div key={template.id} className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {template.template_type.replace('_', ' ').toUpperCase()}
                      </p>
                      <div className="mt-2 flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          template.channel === 'both' ? 'text-blue-600 bg-blue-100' :
                          template.channel === 'sms' ? 'text-green-600 bg-green-100' :
                          'text-purple-600 bg-purple-100'
                        }`}>
                          {getChannelIcon(template.channel)}
                          <span className="ml-1">{template.channel.toUpperCase()}</span>
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          template.is_active ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                        }`}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => { setEditingTemplate(template); setTemplateError(''); setTemplateFormOpen(true) }}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit template"
                        aria-label={`Edit ${template.name}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm(`Delete the template "${template.name}"? Notices that use it will fall back to the built-in wording.`)) deleteTemplateMutation.mutate(template.id) }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {template.subject && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700">Subject:</p>
                      <p className="text-sm text-gray-600">{template.subject}</p>
                    </div>
                  )}
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">Content Preview:</p>
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {template.content.substring(0, 100)}...
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Settings className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a notification template.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              aria-label="Filter by status"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="read">Read</option>
            </select>
            <select
              value={filterChannel}
              onChange={(e) => setFilterChannel(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              aria-label="Filter by channel"
            >
              <option value="all">All Channels</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
          </div>

          {/* Notifications List */}
          {notificationsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading notifications...</p>
            </div>
          ) : notifications && notifications.length > 0 ? (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Channel
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sent At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {notifications?.map((notification: Notification) => (
                        <tr key={notification.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {notification.recipient_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getChannelIcon(notification.channel)}
                              <span className="ml-2 text-sm text-gray-900">
                                {notification.channel.toUpperCase()}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                              {(notification.status ?? 'unknown').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {notification.subject || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {notification.sent_at 
                              ? new Date(notification.sent_at).toLocaleString()
                              : '-'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button 
                              onClick={() => setSelectedNotification(notification)}
                              className="text-primary-600 hover:text-primary-900"
                              title="View notification details"
                              aria-label="View notification details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Bell className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No notifications</h3>
              <p className="mt-1 text-sm text-gray-500">
                No notifications found matching the current filters.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'attendance' && (
        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={() => processAttendanceNotificationsMutation.mutate()}
              disabled={processAttendanceNotificationsMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <Send className="w-4 h-4 mr-2" />
              {processAttendanceNotificationsMutation.isPending ? 'Processing...' : 'Process Daily Notifications'}
            </button>
            <button
              onClick={() => sendWeeklySummariesMutation.mutate()}
              disabled={sendWeeklySummariesMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {sendWeeklySummariesMutation.isPending ? 'Sending...' : 'Send Weekly Summaries'}
            </button>
          </div>

          {/* Attendance Notification Statistics */}
          {attendanceStatsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading attendance notification statistics...</p>
            </div>
          ) : attendanceStats ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Bell className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Notifications
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {attendanceStats.total_notifications}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-6 w-6 text-red-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Absent Alerts
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {attendanceStats.template_breakdown.attendance_absent || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Clock className="h-6 w-6 text-yellow-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Late Alerts
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {attendanceStats.template_breakdown.attendance_late || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Delivered
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {attendanceStats.status_breakdown.delivered || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No attendance notification data</h3>
              <p className="mt-1 text-sm text-gray-500">
                Process attendance notifications to see statistics.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'statistics' && (
        <div className="space-y-6">
          {statsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading statistics...</p>
            </div>
          ) : stats ? (
            <>
              {/* Overall Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Bell className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Total Notifications
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {stats.total_notifications ?? 0}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-6 w-6 text-green-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Delivered
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {stats.status_breakdown?.delivered ?? stats.delivered_notifications ?? 0}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="h-6 w-6 text-red-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Failed
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {stats.status_breakdown?.failed ?? stats.failed_notifications ?? 0}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Clock className="h-6 w-6 text-yellow-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Pending
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {stats.status_breakdown?.pending ?? 0}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Template Breakdown */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Notifications by Template Type
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(stats.template_breakdown ?? {}).map(([template, count]) => (
                      <div key={template} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          {template.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase())}
                        </span>
                        <span className="text-sm text-gray-900">{count}</span>
                      </div>
                    ))}
                    {!Object.keys(stats.template_breakdown ?? {}).length && (
                      <p className="text-sm text-gray-500">Nothing was sent from a template in this period.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Notifications */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Recent Notifications
                  </h3>
                  {stats.recent_notifications?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_notifications.map((notification: Notification) => (
                        <div key={notification.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {getChannelIcon(notification.channel)}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {notification.recipient_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {notification.subject || 'No subject'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                              {(notification.status ?? 'unknown').toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(notification.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No recent notifications</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No statistics data</h3>
              <p className="mt-1 text-sm text-gray-500">
                Statistics will appear once notifications are sent.
              </p>
            </div>
          )}
        </div>
      )}
      {templateFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              const entered = new FormData(event.currentTarget)
              saveTemplate.mutate({
                id: editingTemplate?.id,
                data: {
                  name: String(entered.get('name')).trim(),
                  template_type: String(entered.get('template_type')),
                  channel: String(entered.get('channel')) as Channel,
                  subject: String(entered.get('subject') ?? '').trim(),
                  content: String(entered.get('content')).trim(),
                  is_active: entered.get('is_active') === 'on',
                },
              })
            }}
            className="max-h-full w-full max-w-2xl space-y-4 overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{editingTemplate ? 'Edit template' : 'New template'}</h3>
              <p className="mt-1 text-sm text-gray-500">
                Anything in curly braces is filled in when the notice is sent, for example {'{student_name}'}, {'{student_id}'} and {'{date}'}.
              </p>
            </div>
            {templateError && <div className="rounded bg-red-50 p-3 text-sm text-red-700">{templateError}</div>}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm text-gray-600">Name
                <input required name="name" defaultValue={editingTemplate?.name} className="input mt-1" placeholder="e.g. Fee reminder, first notice" />
              </label>
              <label className="text-sm text-gray-600">Sent for
                <select name="template_type" defaultValue={editingTemplate?.template_type ?? 'general'} className="input mt-1">
                  {TEMPLATE_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="text-sm text-gray-600">Sent by
                <select name="channel" defaultValue={editingTemplate?.channel ?? 'sms'} className="input mt-1">
                  {CHANNELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="text-sm text-gray-600">Subject
                <input name="subject" defaultValue={editingTemplate?.subject} className="input mt-1" placeholder="Used for email; left empty for SMS" />
              </label>
            </div>
            <label className="block text-sm text-gray-600">Message
              <textarea required name="content" rows={8} defaultValue={editingTemplate?.content} className="input mt-1" placeholder="Dear Parent/Guardian, ..." />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" name="is_active" defaultChecked={editingTemplate?.is_active ?? true} />
              In use
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={() => { setTemplateFormOpen(false); setEditingTemplate(null); setTemplateError('') }}>Cancel</button>
              <button className="btn btn-primary" disabled={saveTemplate.isPending}>{saveTemplate.isPending ? 'Saving…' : 'Save template'}</button>
            </div>
          </form>
        </div>
      )}
      {selectedNotification && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className="w-full max-w-lg rounded-lg bg-white shadow-xl"><div className="border-b p-5"><h3 className="text-lg font-semibold">Notification details</h3><p className="text-sm text-gray-500">To {selectedNotification.recipient_name}</p></div><dl className="grid gap-4 p-5 sm:grid-cols-2"><div><dt className="text-xs uppercase text-gray-500">Channel</dt><dd className="uppercase">{selectedNotification.channel}</dd></div><div><dt className="text-xs uppercase text-gray-500">Status</dt><dd className="capitalize">{selectedNotification.status}</dd></div><div className="sm:col-span-2"><dt className="text-xs uppercase text-gray-500">Subject</dt><dd>{selectedNotification.subject || 'No subject'}</dd></div><div className="sm:col-span-2"><dt className="text-xs uppercase text-gray-500">Message</dt><dd className="whitespace-pre-wrap">{selectedNotification.content}</dd></div><div><dt className="text-xs uppercase text-gray-500">Created</dt><dd>{new Date(selectedNotification.created_at).toLocaleString()}</dd></div><div><dt className="text-xs uppercase text-gray-500">Sent</dt><dd>{selectedNotification.sent_at ? new Date(selectedNotification.sent_at).toLocaleString() : 'Not sent'}</dd></div>{selectedNotification.failure_reason && <div className="sm:col-span-2"><dt className="text-xs uppercase text-red-500">Failure reason</dt><dd>{selectedNotification.failure_reason}</dd></div>}</dl><div className="flex justify-end border-t p-4"><button onClick={() => setSelectedNotification(null)} className="btn btn-primary px-4 py-2">Close</button></div></div></div>}
    </div>
  )
}
